/**
 * Global Search Service
 * Handles patient search functionality with proper GraphQL integration
 */

import { AbstractService } from '@/services/core/serviceFactory';
import { searchGraphqlService, type SearchPatientResult as APISearchResult, type EDPatientDetailsResult } from '@/services/api';
import type { GraphQLClientResponse } from '@/services/core/graphqlClient';

export interface PatientSearchResult {
  pep_patient_id: number;
  patient_clinic_data_id: number;
  full_name: string;
  dob: string;
  home_phone_number: string;
  mobile_phone_number: string;
  email: string;
  clinic_id: number;
  clinic_name: string;
  emr_id: string;
}

export interface SearchResponse {
  pep_vw_consolidated_data: APISearchResult[];
}

export class GlobalSearchService extends AbstractService {
  /**
   * Search patients by name using pep_vw_patient_details
   */
  async searchPatients(searchStr: string): Promise<GraphQLClientResponse<SearchResponse & { totalCount?: number }>> {
    this.requireAuth();

    try {
      const response = await searchGraphqlService.searchEDPatientByName(
        this.context.hasuraToken,
        searchStr
      );
      
      if (!response.success || !response.data?.pep_vw_patient_details) {
        return {
          success: false,
          data: { pep_vw_consolidated_data: [], totalCount: 0 }
        } as GraphQLClientResponse<SearchResponse & { totalCount?: number }>;
      }

      const mappedPatients = response.data.pep_vw_patient_details.map(this.mapEDPatientToSearchResult);
      const totalCount = response.data.pep_vw_patient_details_aggregate?.aggregate?.count || 0;
      
      return {
        success: true,
        data: { pep_vw_consolidated_data: mappedPatients, totalCount }
      } as GraphQLClientResponse<SearchResponse & { totalCount?: number }>;
    } catch (error) {
      console.error('Error searching patients:', error);
      throw new Error('Failed to search patients');
    }
  }

  /**
   * Search patients by multiple fields using pep_vw_patient_details
   */
  async searchPatientsByField(searchStr: string): Promise<GraphQLClientResponse<SearchResponse & { totalCount?: number }>> {
    return this.searchPatients(searchStr);
  }

  /**
   * Search patients by date of birth using pep_vw_patient_details
   */
  async searchPatientsByDOB(dateStr: string): Promise<GraphQLClientResponse<SearchResponse & { totalCount?: number }>> {
    this.requireAuth();

    try {
      const response = await searchGraphqlService.searchEDPatientByDOB(
        this.context.hasuraToken,
        dateStr
      );
      
      if (!response.success || !response.data?.pep_vw_patient_details) {
        return {
          success: false,
          data: { pep_vw_consolidated_data: [], totalCount: 0 }
        } as GraphQLClientResponse<SearchResponse & { totalCount?: number }>;
      }

      const mappedPatients = response.data.pep_vw_patient_details.map(this.mapEDPatientToSearchResult);
      const totalCount = response.data.pep_vw_patient_details_aggregate?.aggregate?.count || 0;
      
      return {
        success: true,
        data: { pep_vw_consolidated_data: mappedPatients, totalCount }
      } as GraphQLClientResponse<SearchResponse & { totalCount?: number }>;
    } catch (error) {
      console.error('Error searching patients by DOB:', error);
      throw new Error('Failed to search patients by DOB');
    }
  }

  /**
   * Search patients by specific field using pep_vw_patient_details
   */
  async searchPatientsBySpecificField(searchStr: string, fieldName: string): Promise<GraphQLClientResponse<SearchResponse & { totalCount?: number }>> {
    this.requireAuth();

    try {
      let response;

      switch (fieldName) {
        case 'full_name':
          response = await searchGraphqlService.searchEDPatientByName(this.context.hasuraToken, searchStr);
          break;
        case 'mobile_phone_number':
        case 'home_phone_number':
          response = await searchGraphqlService.searchEDPatientByPhone(this.context.hasuraToken, searchStr);
          break;
        case 'email':
          response = await searchGraphqlService.searchEDPatientByEmail(this.context.hasuraToken, searchStr);
          break;
        case 'clinic_name':
          response = await searchGraphqlService.searchEDPatientByClinic(this.context.hasuraToken, searchStr);
          break;
        case 'emr_id':
          response = await searchGraphqlService.searchEDPatientByEMR(this.context.hasuraToken, searchStr);
          break;
        default:
          response = await searchGraphqlService.searchEDPatientByName(this.context.hasuraToken, searchStr);
          break;
      }

      if (!response.success || !response.data?.pep_vw_patient_details) {
        return {
          success: false,
          data: { pep_vw_consolidated_data: [], totalCount: 0 }
        } as GraphQLClientResponse<SearchResponse & { totalCount?: number }>;
      }

      const mappedPatients = response.data.pep_vw_patient_details.map(this.mapEDPatientToSearchResult);
      const totalCount = response.data.pep_vw_patient_details_aggregate?.aggregate?.count || 0;
      
      return {
        success: true,
        data: { pep_vw_consolidated_data: mappedPatients, totalCount }
      } as GraphQLClientResponse<SearchResponse & { totalCount?: number }>;
    } catch (error) {
      console.error('Error searching patients by specific field:', error);
      throw new Error('Failed to search patients by specific field');
    }
  }

  /**
   * Get patients based on user role
   * All users now use pep_vw_patient_details with pagination
   */
  async getPatientsByUserRole(userId: number, userType: string, offset: number = 0, limit: number = 20): Promise<GraphQLClientResponse<SearchResponse & { totalCount?: number }>> {
    this.requireAuth();

    try {
      // All users use the ED patient details query (pep_vw_patient_details)
      const response = await searchGraphqlService.getEDPatientDetails(this.context.hasuraToken, offset, limit);
      
      if (!response.success || !response.data?.pep_vw_patient_details) {
        return {
          success: false,
          data: { pep_vw_consolidated_data: [], totalCount: 0 }
        } as GraphQLClientResponse<SearchResponse & { totalCount?: number }>;
      }

      // Map ED results to standard format
      const mappedPatients = response.data.pep_vw_patient_details.map(this.mapEDPatientToSearchResult);
      const totalCount = response.data.pep_vw_patient_details_aggregate?.aggregate?.count || 0;
      
      return {
        success: true,
        data: { 
          pep_vw_consolidated_data: mappedPatients,
          totalCount 
        }
      } as GraphQLClientResponse<SearchResponse & { totalCount?: number }>;
    } catch (error) {
      console.error('Error getting patients by user role:', error);
      throw new Error('Failed to get patients');
    }
  }

  /**
   * Search within all workflow patients (no user filter for global search)
   */
  async searchWorkflowPatients(userId: number, searchStr: string, field: string): Promise<GraphQLClientResponse<SearchResponse>> {
    this.requireAuth();

    try {
      let whereCondition: any = {};

      // Add search conditions based on field parameter
      if (searchStr.trim()) {
        switch (field) {
          case 'full_name':
          case 'name':
            whereCondition.patient_full_name = { _ilike: `%${searchStr}%` };
            break;
          case 'emr_id':
          case 'emr':
            whereCondition.emr_id = { _ilike: `%${searchStr}%` };
            break;
          case 'dob':
            // Format date for comparison
            whereCondition.dob = { _eq: searchStr };
            break;
          case 'clinic_name':
          case 'clinic':
            whereCondition.clinic_name = { _ilike: `%${searchStr}%` };
            break;
        }
      }

      const query = `
        query SearchAllWorkflowPatients($where: pep_vw_patient_workflow_user_assignments_bool_exp!) {
          pep_vw_patient_workflow_user_assignments(
            where: $where
            distinct_on: patient_id
            order_by: {patient_id: asc}
          ) {
            patient_id
            patient_full_name
            dob
            emr_id
            clinic_name
            program_type
            workflow_name
          }
        }
      `;

      const { graphqlClient } = await import('@/services/core/graphqlClient');
      const response = await graphqlClient.query(query, { where: whereCondition });

      if (!response.success || response.errors) {
        console.error('Error searching workflow patients:', response.errors);
        return {
          success: false,
          data: { pep_vw_consolidated_data: [] }
        } as GraphQLClientResponse<SearchResponse>;
      }

      const patients = response.data?.pep_vw_patient_workflow_user_assignments || [];

      // Map workflow results to SearchResult format
      const mappedPatients = patients.map((patient: any) => ({
        pep_patient_id: patient.patient_id,
        patient_clinic_data_id: patient.patient_id,
        full_name: patient.patient_full_name,
        dob: patient.dob,
        home_phone_number: null,
        mobile_phone_number: null,
        email: null,
        clinic_id: null,
        clinic_name: patient.clinic_name,
        emr_id: patient.emr_id
      }));

      return {
        success: true,
        data: { pep_vw_consolidated_data: mappedPatients }
      } as GraphQLClientResponse<SearchResponse>;
    } catch (error) {
      console.error('Error searching workflow patients:', error);
      throw new Error('Search failed');
    }
  }

  /**
   * Map ED patient details to standard search result format
   */
  private mapEDPatientToSearchResult(patient: EDPatientDetailsResult): APISearchResult {
    return {
      pep_patient_id: patient.enrollment_patient_id || 0,
      patient_clinic_data_id: patient.emr_patient_clinic_data_id || 0,
      full_name: `${patient.first_name} ${patient.middle_name ? patient.middle_name + ' ' : ''}${patient.last_name}`.trim(),
      dob: patient.dob,
      home_phone_number: patient.home_number,
      mobile_phone_number: patient.mobile_number,
      email: patient.email,
      clinic_id: patient.clinic_id,
      clinic_name: patient.clinic_name,
      emr_id: patient.emr_id
    };
  }

  /**
   * Intelligent search - uses pep_vw_patient_details for all search types
   * Detects pattern type (date, phone, email, name) and routes to appropriate ED search
   */
  async intelligentSearch(searchStr: string): Promise<GraphQLClientResponse<SearchResponse & { totalCount?: number }>> {
    this.requireAuth();

    // Check if it's a date pattern (MM/DD/YYYY, YYYY-MM-DD, etc.)
    const datePattern = /^\d{1,2}\/\d{1,2}\/\d{4}$|^\d{4}-\d{1,2}-\d{1,2}$/;

    if (datePattern.test(searchStr)) {
      // Convert to proper date format if needed
      let dateStr = searchStr;
      if (searchStr.includes('/')) {
        const [month, day, year] = searchStr.split('/');
        dateStr = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      }
      return this.searchPatientsByDOB(dateStr);
    }

    // Check if it looks like a phone number
    const phonePattern = /^\d{3}-?\d{3}-?\d{4}$|^\(\d{3}\)\s?\d{3}-?\d{4}$/;
    if (phonePattern.test(searchStr)) {
      return this.searchPatientsBySpecificField(`%${searchStr}%`, 'mobile_phone_number');
    }

    // Check if it looks like an email
    const emailPattern = /@/;
    if (emailPattern.test(searchStr)) {
      return this.searchPatientsBySpecificField(`%${searchStr}%`, 'email');
    }

    // Default to name search using pep_vw_patient_details
    return this.searchPatients(`%${searchStr}%`);
  }
}